/**
 * Browser-side device identity for OpenClaw gateway pairings.
 *
 * Implements the OpenClaw v2 device-auth handshake. Each host (Paradex,
 * Paradigm) calls `createDeviceIdentityStore({ storageKey, idbName })` with
 * its own namespace so existing user keys keep loading after the move.
 *
 * Prefers a non-extractable WebCrypto Ed25519 `CryptoKey` persisted in
 * IndexedDB (private bytes never leave WebCrypto). Falls back to a legacy
 * `@noble/curves` path that keeps the raw seed in localStorage when the
 * browser doesn't support WebCrypto Ed25519. Wire protocol is identical
 * on both paths.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

import type { LegacyDeviceKeyMigrator } from './device-identity-migration';

/** Version tag for the signed auth payload. */
const DEVICE_AUTH_PAYLOAD_VERSION = 'v2';

const IDB_STORE = 'identity';
const IDB_KEY = 'device';

interface StoredDevice {
  readonly id: string; // hex SHA-256 of raw public key
  readonly privateKey: string; // base64 of raw 32 bytes (legacy path only)
  readonly publicKey: string; // base64 of raw 32 bytes
}

/** Record persisted in IndexedDB for the hardened (WebCrypto) path. */
export interface StoredCryptoKeyDevice {
  readonly id: string;
  readonly publicKey: string; // base64 of raw 32-byte public key
  readonly privateKey: CryptoKey; // non-extractable; structured-cloned by IDB
}

export interface DeviceIdentity {
  readonly id: string;
  /** Base64-encoded raw 32-byte Ed25519 public key. */
  readonly publicKey: string;
  /** Signs a UTF-8 payload; returns base64 of the raw 64-byte signature. */
  readonly sign: (payload: string) => Promise<string>;
}

export interface DeviceHandshakeInput {
  readonly clientId: string;
  /** Client mode — e.g. `'webchat'` for the browser-based terminal. */
  readonly clientMode: string;
  /** Operator role — `'operator'` or `'pairing'`. */
  readonly role: string;
  /** Scopes as comma-joined string, in the exact order the client sends them. */
  readonly scopes: readonly string[];
  /** Auth token; empty string when in pairing mode (unpaired first connect). */
  readonly token: string;
  /** Server-provided nonce from `connect.challenge`. */
  readonly nonce: string;
}

export interface DeviceHandshake {
  readonly id: string;
  /** Base64-encoded raw 32-byte Ed25519 public key. */
  readonly publicKey: string;
  /** Base64-encoded raw 64-byte Ed25519 signature over the v2 payload. */
  readonly signature: string;
  /** Unix timestamp in milliseconds (integer). */
  readonly signedAt: number;
  readonly nonce: string;
}

export interface DeviceIdentityStore {
  getDeviceIdentity(): Promise<DeviceIdentity>;
  resetDeviceIdentityCacheForTests(): void;
  buildDeviceHandshake(input: DeviceHandshakeInput): Promise<DeviceHandshake>;
}

export interface DeviceIdentityStoreConfig {
  /** localStorage key for the legacy raw-bytes fallback path. */
  storageKey: string;
  /** IndexedDB database name for the hardened WebCrypto path. */
  idbName: string;
  /**
   * Optional legacy migrator. When present, an existing pre-WebCrypto
   * key in localStorage is upgraded into IndexedDB (preserving the
   * derived `deviceId` so the gateway keeps recognizing the device).
   */
  legacyMigrator?: LegacyDeviceKeyMigrator;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCodePoint(b);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.codePointAt(i)!;
  return out;
}

/** Decode a base64 Ed25519 seed, returning null if invalid or wrong length. */
function tryDecodeSeed(b64: unknown): Uint8Array | null {
  if (typeof b64 !== 'string') return null;
  try {
    const bytes = base64ToBytes(b64);
    return bytes.length === 32 ? bytes : null;
  } catch {
    return null;
  }
}

async function isWebCryptoEd25519Supported(): Promise<boolean> {
  const g = globalThis as {
    crypto?: { subtle?: unknown };
    indexedDB?: unknown;
  };
  if (g.crypto?.subtle == null || g.indexedDB == null) {
    return false;
  }
  try {
    await crypto.subtle.generateKey({ name: 'Ed25519' }, false, [
      'sign',
      'verify',
    ]);
    return true;
  } catch {
    return false;
  }
}

export function createDeviceIdentityStore(
  config: DeviceIdentityStoreConfig,
): DeviceIdentityStore {
  const { storageKey, idbName, legacyMigrator } = config;
  let cached: Promise<DeviceIdentity> | null = null;

  async function idbOpen(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(idbName, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) {
          req.result.createObjectStore(IDB_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () =>
        reject(req.error ?? new Error('indexedDB open failed'));
    });
  }

  async function idbGet(): Promise<StoredCryptoKeyDevice | null> {
    const db = await idbOpen();
    try {
      return await new Promise<StoredCryptoKeyDevice | null>(
        (resolve, reject) => {
          const tx = db.transaction(IDB_STORE, 'readonly');
          const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
          req.onsuccess = () =>
            resolve((req.result as StoredCryptoKeyDevice | undefined) ?? null);
          req.onerror = () => reject(req.error ?? new Error('idb get failed'));
        },
      );
    } finally {
      db.close();
    }
  }

  async function idbPut(record: StoredCryptoKeyDevice): Promise<void> {
    const db = await idbOpen();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(record, IDB_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('idb put failed'));
      });
    } finally {
      db.close();
    }
  }

  async function generateFreshDeviceRecord(): Promise<StoredCryptoKeyDevice> {
    // This lib's generateKey overload returns CryptoKey | CryptoKeyPair; for
    // an asymmetric algorithm it is always a CryptoKeyPair.
    const pair = (await crypto.subtle.generateKey({ name: 'Ed25519' }, false, [
      'sign',
      'verify',
    ])) as CryptoKeyPair;
    const rawPub = new Uint8Array(
      await crypto.subtle.exportKey('raw', pair.publicKey),
    );
    return {
      id: bytesToHex(sha256(rawPub)),
      publicKey: bytesToBase64(rawPub),
      privateKey: pair.privateKey,
    };
  }

  function cryptoKeyIdentity(record: StoredCryptoKeyDevice): DeviceIdentity {
    return {
      id: record.id,
      publicKey: record.publicKey,
      async sign(payload: string): Promise<string> {
        const sig = new Uint8Array(
          await crypto.subtle.sign(
            'Ed25519',
            record.privateKey,
            new TextEncoder().encode(payload),
          ),
        );
        return bytesToBase64(sig);
      },
    };
  }

  async function loadOrCreateWebCrypto(): Promise<DeviceIdentity> {
    const existing = await idbGet();
    if (
      existing != null &&
      typeof existing.id === 'string' &&
      typeof existing.publicKey === 'string' &&
      existing.privateKey instanceof CryptoKey
    ) {
      return cryptoKeyIdentity(existing);
    }

    const migrated = legacyMigrator
      ? await legacyMigrator.migrateLegacyDeviceKey()
      : null;
    const record = migrated ?? (await generateFreshDeviceRecord());
    await idbPut(record);
    if (migrated != null && legacyMigrator) {
      legacyMigrator.clearLegacyDeviceKey();
    }
    return cryptoKeyIdentity(record);
  }

  function loadOrCreateLegacy(): DeviceIdentity {
    let stored: StoredDevice | null = null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw != null) stored = JSON.parse(raw) as StoredDevice;
    } catch {
      stored = null;
    }

    let privBytes = stored != null ? tryDecodeSeed(stored.privateKey) : null;

    if (
      stored == null ||
      typeof stored.id !== 'string' ||
      typeof stored.publicKey !== 'string' ||
      privBytes == null
    ) {
      const privateKey = ed25519.utils.randomPrivateKey();
      const publicKey = ed25519.getPublicKey(privateKey);
      const id = bytesToHex(sha256(publicKey));
      stored = {
        id,
        privateKey: bytesToBase64(privateKey),
        publicKey: bytesToBase64(publicKey),
      };
      privBytes = privateKey;
      try {
        localStorage.setItem(storageKey, JSON.stringify(stored));
      } catch {
        // Non-persistent identity; signing still works for this session.
      }
    }

    const seed = privBytes;
    return {
      id: stored.id,
      publicKey: stored.publicKey,
      async sign(payload: string): Promise<string> {
        const sig = ed25519.sign(new TextEncoder().encode(payload), seed);
        return bytesToBase64(sig);
      },
    };
  }

  async function loadOrCreate(): Promise<DeviceIdentity> {
    if (await isWebCryptoEd25519Supported()) {
      try {
        return await loadOrCreateWebCrypto();
      } catch {
        // Fall through to the legacy path if IndexedDB is unavailable.
      }
    }
    return loadOrCreateLegacy();
  }

  async function getDeviceIdentity(): Promise<DeviceIdentity> {
    if (cached == null) {
      const pending = loadOrCreate();
      pending.catch(() => {
        if (cached === pending) cached = null;
      });
      cached = pending;
    }
    return cached;
  }

  return {
    getDeviceIdentity,
    resetDeviceIdentityCacheForTests(): void {
      cached = null;
    },
    async buildDeviceHandshake(
      input: DeviceHandshakeInput,
    ): Promise<DeviceHandshake> {
      const identity = await getDeviceIdentity();
      const signedAt = Date.now();
      const scopesField = input.scopes.join(',');

      const payload = [
        DEVICE_AUTH_PAYLOAD_VERSION,
        identity.id,
        input.clientId,
        input.clientMode,
        input.role,
        scopesField,
        String(signedAt),
        input.token,
        input.nonce,
      ].join('|');

      const signature = await identity.sign(payload);

      return {
        id: identity.id,
        publicKey: identity.publicKey,
        signature,
        signedAt,
        nonce: input.nonce,
      };
    },
  };
}
